import React from "react";

import {
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiEye,
  FiFileText,
  FiMoreVertical,
  FiPlus,
  FiTrash2,
  FiClipboard,
  FiList,
} from "react-icons/fi";

import {
  formatRequirementDate,
  getCustomerMobile,
  getCustomerName,
  getRaisedByName,
  getRequirementPriorityClass,
  getRequirementStatusClass,
} from "./constants";

const RequirementTable = ({
  requirements = [],
  loading = false,
  pagination,
  onView,
  onEdit,
  onDelete,
  onChangeStatus,
  onPageChange,
  onLimitChange,
  onAddRequirement,
  onAssignTask,
  onViewTasks,
}) => {
  const safePagination = {
    page: Number(pagination?.page || 1),
    limit: Number(pagination?.limit || 10),
    total: Number(pagination?.total || 0),
    totalPages: Math.max(
      Number(pagination?.totalPages || 1),
      1
    ),
  };

  const getSerialNumber = (index) => {
    return (
      (safePagination.page - 1) *
      safePagination.limit +
      index +
      1
    );
  };

  const getAttachmentCount = (requirement) => {
    return Array.isArray(requirement?.attachments)
      ? requirement.attachments.length
      : 0;
  };

  const getTaskSummary = (
    requirement
  ) => {
    const summary =
      requirement?.taskSummary ||
      {};

    return {
      total: Number(
        summary.totalTasks || 0
      ),

      open: Number(
        summary.openTasks || 0
      ),

      completed: Number(
        summary.completedTasks ||
        0
      ),

      unread: Number(
        summary.unreadTaskCount ||
        0
      ),

      hasUnread: Boolean(
        summary
          .hasUnreadTaskUpdate ||
        Number(
          summary
            .unreadTaskCount ||
          0
        ) > 0
      ),
    };
  };

  const getDescription = (requirement) => {
    const description = String(
      requirement?.description || ""
    ).trim();

    return description || "No description available";
  };

  return (
    <div className="requirement-table-card">
      <div className="requirement-table-scroll">
        <table className="requirement-table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>S.No</th>
              <th>Customer</th>
              <th>Requirement</th>
              <th>Priority</th>
              <th>Raised By</th>
              <th>Status</th>
              <th>Tasks</th>
              <th>Attachments</th>
              <th>Created On</th>
              <th style={{ width: 190 }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={10}
                  className="requirement-table-message"
                >
                  <div className="requirement-loading">
                    <div className="requirement-loading-spinner" />

                    <p>Loading customer requirements...</p>
                  </div>
                </td>
              </tr>
            ) : requirements.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="requirement-table-message"
                >
                  <div className="requirement-empty">
                    <FiFileText />

                    <h3>No Requirements Found</h3>

                    <p>
                      Customer requirements will appear here
                      after you create the first requirement.
                    </p>

                    <button
                      type="button"
                      onClick={onAddRequirement}
                    >
                      <FiPlus />
                      &nbsp;
                      Add Requirement
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              requirements.map((requirement, index) => {
                const taskSummary =
                  getTaskSummary(
                    requirement
                  );
                const customer =
                  requirement?.customer || {};

                const customerName =
                  getCustomerName(customer);

                const customerMobile =
                  getCustomerMobile(customer);

                const customerInitial = String(
                  customerName || "C"
                )
                  .charAt(0)
                  .toUpperCase();

                const description =
                  getDescription(requirement);

                return (
                  <tr
                    key={
                      requirement?._id ||
                      index
                    }
                    className={
                      taskSummary.hasUnread
                        ? "requirement-row-task-update"
                        : taskSummary.open > 0
                          ? "requirement-row-has-task"
                          : ""
                    }
                  >
                    <td className="requirement-table-serial">
                      {getSerialNumber(index)}
                    </td>

                    <td>
                      <div className="requirement-customer">
                        <div className="requirement-avatar">
                          {customerInitial}
                        </div>

                        <div className="requirement-customer-info">
                          <strong title={customerName}>
                            {customerName}
                          </strong>

                          <span>
                            {customerMobile}
                          </span>

                          {customer?.companyName ? (
                            <small
                              title={customer.companyName}
                            >
                              {customer.companyName}
                            </small>
                          ) : null}
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="requirement-table-info">
                        <strong
                          title={
                            requirement?.requirementType
                          }
                        >
                          {requirement?.requirementType ||
                            "-"}
                        </strong>

                        <span title={description}>
                          {description}
                        </span>
                      </div>
                    </td>

                    <td>
                      <span
                        className={getRequirementPriorityClass(
                          requirement?.priority
                        )}
                      >
                        {requirement?.priority || "Medium"}
                      </span>
                    </td>

                    <td>
                      <div className="requirement-raised-by">
                        <strong>
                          {getRaisedByName(requirement)}
                        </strong>

                        <span>
                          {requirement?.raisedBy ||
                            "customer"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="requirement-task-cell">
                        {taskSummary.total > 0 ? (
                          <button
                            type="button"
                            className={`requirement-task-count ${taskSummary.hasUnread
                              ? "has-unread"
                              : ""
                              }`}
                            onClick={() =>
                              onViewTasks?.(
                                requirement
                              )
                            }
                            title="View linked tasks"
                          >
                            <FiList />

                            <span>
                              {taskSummary.total}
                            </span>

                            {taskSummary.open >
                              0 ? (
                              <small>
                                {
                                  taskSummary.open
                                }{" "}
                                open
                              </small>
                            ) : null}

                            {taskSummary.hasUnread ? (
                              <strong>
                                {
                                  taskSummary.unread
                                }{" "}
                                new
                              </strong>
                            ) : null}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="requirement-no-task-button"
                            onClick={() =>
                              onAssignTask?.(
                                requirement
                              )
                            }
                          >
                            No tasks
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={getRequirementStatusClass(
                          requirement?.status
                        )}
                        title="Click to change status"
                        onClick={() =>
                          onChangeStatus(requirement)
                        }
                      >
                        {requirement?.status || "Active"}
                      </button>
                    </td>

                    <td>
                      <div className="requirement-attachment-cell">
                        <FiFileText />

                        <span>
                          {getAttachmentCount(requirement)}
                        </span>
                      </div>
                    </td>

                    <td>
                      <span className="requirement-created-date">
                        {formatRequirementDate(
                          requirement?.createdAt
                        )}
                      </span>
                    </td>

                    <td>
                      <div className="requirement-actions">
                        <button
                          type="button"
                          className="requirement-action action-view"
                          title="View requirement"
                          onClick={() =>
                            onView(requirement)
                          }
                        >
                          <FiEye />
                        </button>

                        <button
                          type="button"
                          className="requirement-action action-edit"
                          title="Edit requirement"
                          onClick={() =>
                            onEdit(requirement)
                          }
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          type="button"
                          className="requirement-action action-status"
                          title="Change status"
                          onClick={() =>
                            onChangeStatus(requirement)
                          }
                        >
                          <FiMoreVertical />
                        </button>
                        <button
                          type="button"
                          className="requirement-action action-assign-task"
                          title="Assign task to employee"
                          onClick={() =>
                            onAssignTask?.(
                              requirement
                            )
                          }
                        >
                          <FiClipboard />
                        </button>

                        {taskSummary.total > 0 ? (
                          <button
                            type="button"
                            className="requirement-action action-view-tasks"
                            title="View linked tasks"
                            onClick={() =>
                              onViewTasks?.(
                                requirement
                              )
                            }
                          >
                            <FiList />

                            {taskSummary.hasUnread ? (
                              <span className="requirement-task-notification-dot" />
                            ) : null}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="requirement-action action-delete"
                          title="Delete requirement"
                          onClick={() =>
                            onDelete(requirement)
                          }
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && safePagination.total > 0 ? (
        <div className="requirement-pagination">
          <div className="requirement-pagination-left">
            Showing{" "}
            <strong>
              {(safePagination.page - 1) *
                safePagination.limit +
                1}
            </strong>{" "}
            to{" "}
            <strong>
              {Math.min(
                safePagination.page *
                safePagination.limit,
                safePagination.total
              )}
            </strong>{" "}
            of{" "}
            <strong>
              {safePagination.total}
            </strong>{" "}
            requirements
          </div>

          <div className="requirement-pagination-right">
            <select
              value={safePagination.limit}
              onChange={onLimitChange}
              aria-label="Requirements per page"
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>

            <button
              type="button"
              disabled={safePagination.page <= 1}
              onClick={() =>
                onPageChange(
                  safePagination.page - 1
                )
              }
              title="Previous page"
            >
              <FiChevronLeft />
            </button>

            <span>
              Page{" "}
              <strong>
                {safePagination.page}
              </strong>{" "}
              of{" "}
              <strong>
                {safePagination.totalPages}
              </strong>
            </span>

            <button
              type="button"
              disabled={
                safePagination.page >=
                safePagination.totalPages
              }
              onClick={() =>
                onPageChange(
                  safePagination.page + 1
                )
              }
              title="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RequirementTable;